import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { refreshOperatorsMirror, verifyPinOffline } from '@/lib/operatorsMirror';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fase 7: Mapeamento de rotas permitidas por role
// Fase 23: role 'cozinha' adicionada (visão focada em KDS)
//          e garçom agora acessa /delivery-admin + /delivery (cardápio externo)
export const ROLE_ROUTES = {
  admin: ['*'], // acesso total
  garcom: ['/garcom', '/delivery-admin', '/delivery'],
  caixa: ['/caixa', '/cozinha', '/relatorios', '/historico-caixa'],
  cozinha: ['/cozinha'],
  entregador: ['/entregador'],
};

// Home default por role (rota para onde vai após login)
export const ROLE_HOME = {
  admin: '/admin',
  garcom: '/garcom',
  caixa: '/caixa',
  cozinha: '/cozinha',
  entregador: '/entregador',
};

export function canAccess(role, path) {
  if (!role) return false;
  const allowed = ROLE_ROUTES[role] || [];
  if (allowed.includes('*')) return true;
  return allowed.includes(path);
}

export function AuthProvider({ children }) {
  const [operator, setOperator] = useState(null);
  const [loading, setLoading] = useState(true);
  // v7.89 — PIN da sessão em MEMÓRIA (nunca persistido): usado pela aba
  // Equipe pra autorizar mutações no servidor sem redigitar o PIN Master.
  const sessionPinRef = useRef(null);

  useEffect(() => {
    const saved = sessionStorage.getItem('kaven_operator');
    if (saved) {
      try {
        setOperator(JSON.parse(saved));
      } catch {
        sessionStorage.removeItem('kaven_operator');
      }
    }
    setLoading(false);
    // Atualiza o espelho de operadores em background (elimina fantasmas locais)
    refreshOperatorsMirror();
  }, []);

  const _finishLogin = (opData, pin) => {
    sessionPinRef.current = pin;
    setOperator(opData);
    sessionStorage.setItem('kaven_operator', JSON.stringify(opData));
    return { success: true, operator: opData };
  };

  // v7.89 — Login: servidor é a autoridade (hash bcrypt no Mongo);
  // offline cai pro espelho local (bcryptjs). PIN nunca é comparado em claro.
  const login = async (pin) => {
    if (API_URL) {
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 6000);
        const resp = await fetch(`${API_URL}/api/operators/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pin }),
          signal: ctrl.signal,
        });
        clearTimeout(t);
        if (resp.status === 401) return { success: false };
        if (resp.status === 403) return { success: false, error: 'inactive' };
        if (resp.ok) {
          const body = await resp.json();
          const op = body.operator || {};
          refreshOperatorsMirror(); // espelho atualizado pra login offline futuro
          return _finishLogin({
            id: op.opId,
            opId: op.opId,
            name: op.name,
            role: op.role || 'admin',
            isMaster: !!op.isMaster,
          }, pin);
        }
      } catch {
        // servidor inacessível — segue pro fallback offline
      }
    }
    const found = await verifyPinOffline(pin);
    if (!found) return { success: false };
    if (found.active === false) return { success: false, error: 'inactive' };
    return _finishLogin({
      id: found.opId || found.id,
      opId: found.opId || String(found.id),
      name: found.name,
      role: found.role || 'admin',
      isMaster: !!found.isMaster,
    }, pin);
  };

  const logout = () => {
    sessionPinRef.current = null;
    setOperator(null);
    sessionStorage.removeItem('kaven_operator');
  };

  const getSessionPin = () => sessionPinRef.current;

  return (
    <AuthContext.Provider value={{ operator, login, logout, loading, getSessionPin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
